// @file Library upsell pinia store
import { calculateLibraryPlanTotalPrice } from '@@/bits/billing_helper'
import { captureFetchException } from '@@/bits/error_tracker'
import { translatePrice } from '@@/bits/intl'
import { generateLibraryCheckoutUrl } from '@@/bits/library_plans_helper'
import { navigateTo } from '@@/bits/location'
import * as PadletApi from '@@/dashboard/padlet_api'
import { Checkout as CheckoutApi } from '@@/dashboard/padlet_api'
import type { LibraryType } from '@@/enums'
import type { Library, LibraryId, LibraryPlan } from '@@/types'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

interface HashedLibraryPlan {
  annual: LibraryPlan
  monthly: LibraryPlan
}

type HashedLibraryPlans = Record<'gold', HashedLibraryPlan>

interface FetchLibraryPlansArgs {
  libraryId?: LibraryId
}

export enum UpgradeStep {
  ChooseTier,
  ChooseTierNoNeon,
  ChooseTierPadletQuota,
  ChooseTierFileSizeQuota,
  ScheduleTeamGold,
}

export enum UpgradeSource {
  UntrashWallQuota = 'UntrashWallQuota',
  UnarchiveWallQuota = 'UnarchiveWallQuota',
  CreateTemplate = 'CreateTemplate',
}

const DEFAULT_PLANS = (): HashedLibraryPlans => ({
  gold: { annual: {} as LibraryPlan, monthly: {} as LibraryPlan },
})

export const useLibraryPlansStore = defineStore('libraryPlans', () => {
  const plans = ref<HashedLibraryPlans>(DEFAULT_PLANS())
  const xUpgradeModal = ref(false)
  const upgradeStep = ref<UpgradeStep | null>(null)
  const upgradeSource = ref<UpgradeSource | null>(null)
  const library = ref<Library | null>(null)
  const makersCount = ref<number | null>(null)
  const wallsUsed = ref<number | null>(null)
  const upgradeLibrarySlug = ref<string | null>(null)
  const plansFetched = computed<boolean>(() => Object.keys(plans.value.gold.annual).length !== 0)
  const libraryType = computed(() => library.value?.libraryType)
  const isDeprecatedFreeTier = computed(() => library.value?.isDeprecatedFreeTier)
  const isLibrarySubscriptionCancelled = computed(() => library.value?.isPlanCancelled)
  const isLibraryOverQuota = computed(() => library.value?.quota.quotaHit)
  const isLibraryTrialExpired = computed(() => library.value?.isTrialExpired)

  function translatedPlanPrice() {
    return ({ tier, period, makersCount }: { tier: string; period: string; makersCount: number }): string | null => {
      const plan = plans.value[tier][period]
      if (plan.price == null || plan.currency == null) return null
      return translatePrice(
        calculateLibraryPlanTotalPrice({
          makersCount,
          price: parseFloat(plan.price),
        }),
        plan.currency,
      )
    }
  }

  function translatedPlanAnnualDiscount({ tier, makersCount }: { tier: string; makersCount: number }): string | null {
    const monthlyPlan = plans.value[tier].monthly
    const annualPlan = plans.value[tier].annual
    if (
      monthlyPlan.price == null ||
      monthlyPlan.currency == null ||
      annualPlan.price == null ||
      annualPlan.currency == null
    )
      return null
    const monthlyPlanTotalPrice = calculateLibraryPlanTotalPrice({
      makersCount,
      price: parseFloat(monthlyPlan.price),
    })
    const annualPlanTotalPrice = calculateLibraryPlanTotalPrice({
      makersCount,
      price: parseFloat(annualPlan.price),
    })
    return translatePrice(monthlyPlanTotalPrice * 12 - annualPlanTotalPrice, monthlyPlan.currency)
  }

  async function fetchPlans({ libraryId }: FetchLibraryPlansArgs): Promise<void> {
    try {
      const response =
        libraryId != null
          ? await PadletApi.LibraryPlan.fetchByLibraryId({ libraryId })
          : await PadletApi.LibraryPlan.fetch()
      const fetchedPlans = response.data as unknown as LibraryPlan[]
      const hashedPlans = {
        gold: { annual: {} as LibraryPlan, monthly: {} as LibraryPlan },
      }
      for (const plan of fetchedPlans) {
        plan.formattedPrice = translatePrice(parseFloat(plan.price), plan.currency)
        hashedPlans[plan.tier][plan.period] = plan
      }
      plans.value = hashedPlans
    } catch (e) {
      captureFetchException(e, { source: 'LibraryPlansFetchPlans' })
    }
  }

  // FETCH PLANS ACTIONS
  async function fetchPlansAndShowQuotaUpsellModal(payload: {
    upgradeStep: UpgradeStep
    upgradeSource: UpgradeSource
    library: Library
  }): Promise<void> {
    resetUpgradeModalState()
    void fetchPlans({ libraryId: payload.library.id })
    setUpgradeLibrarySlug({ librarySlug: payload.library.slug })
    library.value = payload.library

    showUpgradeModal({
      upgradeStep: payload.upgradeStep,
      upgradeSource: payload.upgradeSource,
      makersCount: payload.library.makersCount as number,
      wallsUsed: payload.library.quota.wallsUsed,
    })
  }

  async function fetchPlansAndShowUpgradeModal(payload: { upgradeStep: UpgradeStep; library: Library }): Promise<void> {
    resetUpgradeModalState()
    library.value = payload.library
    void fetchPlans({ libraryId: payload.library.id })

    // We only show the choose tier modal for libraries created before team gold trial is launched
    // and if the library is considered deprecated free tier
    if (!payload.library.isDeprecatedFreeTier) {
      showUpgradeToTeamGoldModal({
        makersCount: payload.library.makersCount as number,
        wallsUsed: payload.library.quota.wallsUsed,
      })
    } else {
      showUpgradeModal({
        upgradeStep: payload.upgradeStep,
        makersCount: payload.library.makersCount as number,
        wallsUsed: payload.library.quota.wallsUsed,
      })
    }

    setUpgradeLibrarySlug({ librarySlug: payload.library.slug })
  }

  async function fetchPlansAndNavigateToCheckoutUrl({
    libraryId,
    libraryType,
  }: {
    libraryId: LibraryId
    libraryType?: LibraryType
  }): Promise<void> {
    await fetchPlans({ libraryId })
    navigateTo(generateLibraryCheckoutUrl({ libraryId, libraryType, baseCheckoutUrl: plans.value.gold.annual.url }))
  }

  // UPGRADE MODAL ACTIONS
  function showUpgradeModal(payload: {
    upgradeStep: UpgradeStep
    upgradeSource?: UpgradeSource | null
    makersCount: number
    wallsUsed: number
  }): void {
    makersCount.value = payload.makersCount
    wallsUsed.value = payload.wallsUsed
    upgradeStep.value = payload.upgradeStep
    upgradeSource.value = payload.upgradeSource ?? null
    xUpgradeModal.value = true
  }

  function showUpgradeToTeamGoldModal(payload: { makersCount: number; wallsUsed: number }): void {
    showUpgradeModal({
      upgradeStep: UpgradeStep.ScheduleTeamGold,
      makersCount: payload.makersCount,
      wallsUsed: payload.wallsUsed,
    })
  }

  function resetUpgradeModalState(): void {
    makersCount.value = null
    wallsUsed.value = null
    library.value = null
  }

  function closeUpgradeModal(): void {
    xUpgradeModal.value = false
    resetUpgradeModalState()
  }

  async function fetchPlansAndNavigateToStripeCheckoutUrl(payload: {
    libraryId: LibraryId
    nextUrl: string
  }): Promise<void> {
    await fetchPlans({ libraryId: payload.libraryId })

    const response = await CheckoutApi.fetchCheckoutPageUrl({
      libraryId: payload.libraryId.toString(),
      priceId: plans.value.gold.annual.id,
      nextUrl: payload.nextUrl,
    })
    navigateTo(response.checkoutPageUrl)
  }

  function closeUpgradeModalAndResetPlans(): void {
    closeUpgradeModal()
    plans.value = DEFAULT_PLANS()
  }

  function changeUpgradeModalStep(payload: { upgradeStep: UpgradeStep }): void {
    upgradeStep.value = payload.upgradeStep
  }

  async function navigateToStripeCheckoutUrl(payload: {
    plan: LibraryPlan
    libraryId: string
    nextUrl: string
  }): Promise<void> {
    const response = await CheckoutApi.fetchCheckoutPageUrl({
      priceId: payload.plan.id,
      libraryId: payload.libraryId,
      nextUrl: payload.nextUrl,
    })
    navigateTo(response.checkoutPageUrl)
  }

  function setUpgradeLibrarySlug({ librarySlug }: { librarySlug: string }): void {
    upgradeLibrarySlug.value = librarySlug
  }

  return {
    // state
    plans,
    // getters
    plansFetched,
    xUpgradeModal,
    makersCount,
    wallsUsed,
    upgradeStep,
    libraryType,
    upgradeSource,
    isLibrarySubscriptionCancelled,
    isLibraryOverQuota,
    isLibraryTrialExpired,
    isDeprecatedFreeTier,
    library,
    // actions
    changeUpgradeModalStep,
    translatedPlanPrice,
    translatedPlanAnnualDiscount,
    fetchPlans,
    fetchPlansAndShowQuotaUpsellModal,
    fetchPlansAndShowUpgradeModal,
    fetchPlansAndNavigateToCheckoutUrl,
    closeUpgradeModal,
    closeUpgradeModalAndResetPlans,
    navigateToStripeCheckoutUrl,
    fetchPlansAndNavigateToStripeCheckoutUrl,
    showUpgradeToTeamGoldModal,
    setUpgradeLibrarySlug,
    showUpgradeModal,
    resetUpgradeModalState,
  }
})

export { UpgradeSource as LibraryUpgradeSource, UpgradeStep as LibraryUpgradeStep }
export type { HashedLibraryPlans }
